<template>
  <div>
    <Nav></Nav>

    <b-container fluid>
      <b-row align-v="center" class="mt-3 mb-3">
        <b-col>
          <h2 class="mb-0">Plataformas → Tiendas</h2>
          <p class="text-muted mb-0">
            Cada tienda liga una sucursal con una plataforma de delivery: qué menú publica,
            en qué área entran sus órdenes, con qué mesero se registran y con qué credenciales
            se habla con la plataforma.
          </p>
        </b-col>
        <b-col cols="auto">
          <b-button variant="outline-secondary" class="mr-2" :disabled="loading" @click="load">
            <b-icon-arrow-clockwise></b-icon-arrow-clockwise> Actualizar
          </b-button>
          <b-button v-if="canWrite" variant="success" @click="openCreate">
            <b-icon-plus></b-icon-plus> Nueva tienda
          </b-button>
        </b-col>
      </b-row>

      <b-alert v-if="!canWrite" show variant="light" class="border">
        <small class="text-muted">
          Tienes acceso de solo lectura a esta pantalla. Para editar se requiere el permiso
          <code>rrn:central:platform:store:write</code>.
        </small>
      </b-alert>

      <!-- Filtros -->
      <b-card class="mb-3">
        <b-row>
          <b-col cols="12" md="4">
            <b-form-group label="Buscar:" class="mb-0">
              <b-form-input
                type="search"
                v-model="search"
                debounce="300"
                placeholder="Nombre o ID en la plataforma...">
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="Plataforma:" class="mb-0">
              <b-form-select v-model="filterPlatformId">
                <b-form-select-option :value="''">Todas</b-form-select-option>
                <b-form-select-option v-for="p in options.platforms" :key="p.id" :value="p.id">
                  {{ p.name }}
                </b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="Sucursal:" class="mb-0">
              <b-form-select v-model="filterSucursalId">
                <b-form-select-option :value="''">Todas</b-form-select-option>
                <b-form-select-option v-for="s in options.sucursals" :key="s.id" :value="s.id">
                  {{ s.name }}
                </b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
      </b-card>

      <b-card v-if="!loading && stores.length === 0" class="text-center py-5">
        <b-icon-shop font-scale="3" variant="secondary"></b-icon-shop>
        <h4 class="mt-3">Aún no hay tiendas configuradas</h4>
        <p class="text-muted mb-3">
          Da de alta una tienda por cada combinación de sucursal y plataforma que opere delivery.
        </p>
        <b-button v-if="canWrite" variant="success" @click="openCreate">
          <b-icon-plus></b-icon-plus> Crear la primera tienda
        </b-button>
      </b-card>

      <b-table
        v-else
        small
        responsive
        striped
        hover
        :items="filteredStores"
        :fields="fields"
        :busy="loading">
        <template #table-busy>
          <div class="text-center my-3">
            <b-spinner class="align-middle"></b-spinner>
            <strong class="ml-2">Cargando...</strong>
          </div>
        </template>

        <template #cell(platform_id)="data">
          <b-badge variant="primary">{{ platformName(data.item.platform_id) }}</b-badge>
        </template>
        <template #cell(sucursal_id)="data">
          {{ sucursalName(data.item.sucursal_id) }}
        </template>
        <template #cell(menu_id)="data">
          {{ menuName(data.item.menu_id) }}
        </template>
        <template #cell(restaurant_area_id)="data">
          {{ areaName(data.item.restaurant_area_id) }}
        </template>
        <template #cell(waiter_id)="data">
          {{ waiterName(data.item.waiter_id) }}
        </template>
        <template #cell(credentials)="data">
          <b-badge v-if="data.item.has_app_key" variant="success" class="mr-1">app_key</b-badge>
          <b-badge v-if="data.item.has_webhook_secret" variant="success" class="mr-1">webhook</b-badge>
          <span v-if="!data.item.has_app_key && !data.item.has_webhook_secret" class="text-muted">—</span>
        </template>
        <template #cell(buttons)="data">
          <b-button size="sm" variant="warning" class="mr-1" @click="openEdit(data.item)">
            {{ canWrite ? 'Editar' : 'Ver' }}
          </b-button>
          <b-button v-if="canDelete" size="sm" variant="danger" @click="confirmDelete(data.item)">
            Eliminar
          </b-button>
        </template>
      </b-table>
    </b-container>

    <!-- Alta / edición -->
    <b-modal
      v-model="showEditor"
      :title="editing.id ? 'Editar tienda: ' + editing.name : 'Nueva tienda de plataforma'"
      size="lg"
      :ok-title="canWrite ? 'Guardar' : 'Cerrar'"
      :ok-only="!canWrite"
      cancel-title="Cancelar"
      ok-variant="success"
      :ok-disabled="canWrite && (!formValid || saving)"
      @ok.prevent="save">
      <b-row>
        <b-col md="6">
          <b-form-group label="Nombre:" :state="nameState" invalid-feedback="El nombre es requerido">
            <b-form-input v-model="editing.name" :state="nameState" :disabled="!canWrite"
              placeholder="Ej. Uber — Sucursal Centro"></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            label="Plataforma:"
            :state="platformState"
            invalid-feedback="Selecciona la plataforma"
            description="El catálogo de plataformas lo damos de alta nosotros; si necesitas una integración nueva, contáctanos.">
            <b-form-select v-model="editing.platform_id" :state="platformState" :disabled="!canWrite">
              <b-form-select-option :value="0">— selecciona —</b-form-select-option>
              <b-form-select-option v-for="p in options.platforms" :key="p.id" :value="p.id">
                {{ p.name }} ({{ p.code }})
              </b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group
        label="ID de la tienda en la plataforma:"
        :state="platformStoreIdState"
        invalid-feedback="Requerido: es el identificador que manda la plataforma en sus webhooks"
        description="Debe coincidir exactamente con el store id que envía la plataforma; es la llave con la que se asocian sus órdenes.">
        <b-form-input v-model="editing.platform_store_id" :state="platformStoreIdState"
          :disabled="!canWrite"></b-form-input>
      </b-form-group>

      <hr />
      <h6 class="text-muted">Destino de las órdenes</h6>
      <b-row>
        <b-col md="6">
          <b-form-group label="Sucursal:" description="A qué sucursal se envían las órdenes de esta tienda.">
            <b-form-select v-model="editing.sucursal_id" :disabled="!canWrite">
              <b-form-select-option :value="0">— sin asignar —</b-form-select-option>
              <b-form-select-option v-for="s in options.sucursals" :key="s.id" :value="s.id">
                {{ s.name }}
              </b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Menú publicado:" description="El menú que se envía a la plataforma para esta tienda.">
            <b-form-select v-model="editing.menu_id" :disabled="!canWrite">
              <b-form-select-option :value="''">— sin asignar —</b-form-select-option>
              <b-form-select-option v-for="mn in options.menus" :key="mn.id" :value="mn.id">
                {{ mn.name }}
              </b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Área de restaurante:" description="Área con la que se crea el ticket en el POS local.">
            <b-form-select v-model="editing.restaurant_area_id" :disabled="!canWrite">
              <b-form-select-option :value="0">— sin asignar —</b-form-select-option>
              <b-form-select-option v-for="a in options.restaurant_areas" :key="a.id" :value="a.id">
                {{ a.name }}
              </b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Mesero:" description="Mesero al que se le asignan las órdenes de esta tienda.">
            <b-form-select v-model="editing.waiter_id" :disabled="!canWrite">
              <b-form-select-option :value="null">— sin asignar —</b-form-select-option>
              <b-form-select-option v-for="wt in options.waiters" :key="wt.id" :value="wt.id">
                {{ wt.name }}
              </b-form-select-option>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-row>

      <template v-if="canEditSecrets">
        <hr />
        <h6 class="text-muted">Credenciales de la plataforma</h6>
        <b-alert show variant="light" class="border">
          <small class="text-muted">
            Un campo que muestra <code>{{ secretMask }}</code> conserva el valor guardado.
            Escribe encima para reemplazarlo, o déjalo vacío para borrarlo.
          </small>
        </b-alert>
        <b-row>
          <b-col md="6">
            <b-form-group label="app_key:" description="Llave de aplicación con la que se firman las llamadas salientes.">
              <b-form-input v-model="editing.app_key" :disabled="!canWrite"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="webhook_secret:" description="Secreto con el que se valida la firma de los webhooks entrantes.">
              <b-form-input v-model="editing.webhook_secret" :disabled="!canWrite"></b-form-input>
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="base_url:" description="URL base del API de la plataforma para esta tienda.">
              <b-form-input v-model="editing.base_url" :disabled="!canWrite"
                placeholder="https://..."></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
      </template>
      <template v-else>
        <hr />
        <p class="text-muted mb-0">
          <b-icon-lock></b-icon-lock>
          Las credenciales de esta tienda
          ({{ editing.has_app_key || editing.has_webhook_secret ? 'configuradas' : 'sin configurar' }})
          están ocultas. Requieren el permiso <code>rrn:central:platform:store:secrets</code>.
        </p>
      </template>
    </b-modal>
  </div>
</template>

<script>
import Nav from "../../components/Nav.vue";
import platformStoreService from "../../services/platformStoreService";

// Lo que el backend manda en vez de una credencial guardada y lo que espera de
// vuelta para dejarla como está.
const SECRET_MASK = "********";

const emptyStore = () => ({
  id: "",
  name: "",
  platform_id: 0,
  platform_store_id: "",
  sucursal_id: 0,
  menu_id: "",
  restaurant_area_id: 0,
  waiter_id: null,
  app_key: "",
  webhook_secret: "",
  base_url: "",
  has_app_key: false,
  has_webhook_secret: false,
});

export default {
  name: "PlatformStores",
  components: { Nav },
  data() {
    return {
      loading: true,
      saving: false,
      stores: [],
      options: {
        platforms: [],
        menus: [],
        sucursals: [],
        restaurant_areas: [],
        waiters: [],
        can_edit_secrets: false,
      },
      search: "",
      filterPlatformId: "",
      filterSucursalId: "",
      showEditor: false,
      editing: emptyStore(),
      secretMask: SECRET_MASK,
      fields: [
        { key: "name", label: "Nombre", sortable: true },
        { key: "platform_id", label: "Plataforma", sortable: true },
        { key: "platform_store_id", label: "ID en plataforma" },
        { key: "sucursal_id", label: "Sucursal", sortable: true },
        { key: "menu_id", label: "Menú" },
        { key: "restaurant_area_id", label: "Área" },
        { key: "waiter_id", label: "Mesero" },
        { key: "credentials", label: "Credenciales" },
        { key: "buttons", label: "" },
      ],
    };
  },
  computed: {
    canWrite() {
      return this.hasPermission("rrn:central:platform:store:write");
    },
    canDelete() {
      return this.hasPermission("rrn:central:platform:store:delete");
    },
    // El backend es la autoridad (manda can_edit_secrets); el permiso local sólo
    // evita parpadeos mientras carga.
    canEditSecrets() {
      return this.options.can_edit_secrets || this.hasPermission("rrn:central:platform:store:secrets");
    },
    filteredStores() {
      const needle = this.search.trim().toLowerCase();
      return this.stores.filter((s) => {
        if (this.filterPlatformId !== "" && s.platform_id !== this.filterPlatformId) return false;
        if (this.filterSucursalId !== "" && s.sucursal_id !== this.filterSucursalId) return false;
        if (!needle) return true;
        return (
          (s.name || "").toLowerCase().includes(needle) ||
          (s.platform_store_id || "").toLowerCase().includes(needle)
        );
      });
    },
    nameState() {
      return this.editing.name.trim().length > 0;
    },
    platformState() {
      return !!this.editing.platform_id;
    },
    platformStoreIdState() {
      return this.editing.platform_store_id.trim().length > 0;
    },
    formValid() {
      return this.nameState && this.platformState && this.platformStoreIdState;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      Promise.all([platformStoreService.list(), platformStoreService.options()])
        .then(([storesRes, optionsRes]) => {
          this.stores = storesRes.data || [];
          this.options = Object.assign({}, this.options, optionsRes.data || {});
        })
        .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"))
        .finally(() => {
          this.loading = false;
        });
    },
    openCreate() {
      this.editing = emptyStore();
      this.showEditor = true;
    },
    openEdit(store) {
      this.editing = Object.assign(emptyStore(), store, {
        // El backend devuelve null en los campos sin valor; los inputs quieren "".
        menu_id: store.menu_id || "",
        app_key: store.app_key || "",
        webhook_secret: store.webhook_secret || "",
        base_url: store.base_url || "",
      });
      this.showEditor = true;
    },
    save() {
      if (!this.canWrite) {
        this.showEditor = false;
        return;
      }
      if (!this.formValid) return;

      this.saving = true;
      const payload = {
        name: this.editing.name.trim(),
        platform_id: this.editing.platform_id,
        platform_store_id: this.editing.platform_store_id.trim(),
        sucursal_id: this.editing.sucursal_id || 0,
        menu_id: this.editing.menu_id || "",
        restaurant_area_id: this.editing.restaurant_area_id || 0,
        waiter_id: this.editing.waiter_id || null,
      };
      if (this.canEditSecrets) {
        // Se manda el valor tal cual: la máscara el backend la lee como "no
        // cambies" y la cadena vacía como "bórrala". Omitir el campo también
        // conserva el valor, así que quien no puede verlos nunca los pisa.
        payload.app_key = this.editing.app_key;
        payload.webhook_secret = this.editing.webhook_secret;
        payload.base_url = this.editing.base_url;
      }

      const req = this.editing.id
        ? platformStoreService.update(this.editing.id, payload)
        : platformStoreService.create(payload);

      req
        .then(() => {
          this.makeToast("Guardado", "Tienda " + (this.editing.id ? "actualizada" : "creada"), "success");
          this.showEditor = false;
          this.load();
        })
        .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"))
        .finally(() => {
          this.saving = false;
        });
    },
    confirmDelete(store) {
      this.$bvModal
        .msgBoxConfirm(
          'Se eliminará la tienda "' + store.name + '". Sus órdenes históricas se conservan, pero quedan sin tienda asociada.',
          {
            title: "Eliminar tienda",
            okVariant: "danger",
            okTitle: "Eliminar",
            cancelTitle: "Cancelar",
            centered: true,
          }
        )
        .then((ok) => {
          if (!ok) return;
          platformStoreService
            .remove(store.id)
            .then(() => {
              this.makeToast("Eliminada", "La tienda fue eliminada", "success");
              this.load();
            })
            .catch((err) => this.makeToast("Error", this.errMsg(err), "danger"));
        })
        .catch(() => {});
    },
    platformName(id) {
      const p = this.options.platforms.find((x) => x.id === id);
      return p ? p.name : "#" + id;
    },
    sucursalName(id) {
      if (!id) return "—";
      const s = this.options.sucursals.find((x) => x.id === id);
      return s ? s.name : "#" + id;
    },
    menuName(id) {
      if (!id) return "—";
      const mn = this.options.menus.find((x) => x.id === id);
      return mn ? mn.name : id;
    },
    areaName(id) {
      if (!id) return "—";
      const a = this.options.restaurant_areas.find((x) => x.id === id);
      return a ? a.name : "#" + id;
    },
    waiterName(id) {
      if (!id) return "—";
      const wt = this.options.waiters.find((x) => x.id === id);
      return wt ? wt.name : "#" + id;
    },
    errMsg(err) {
      if (err && err.response && err.response.data) {
        return typeof err.response.data === "string"
          ? err.response.data
          : err.response.data.error || JSON.stringify(err.response.data);
      }
      return err && err.message ? err.message : "Error desconocido";
    },
    makeToast(title, content, variant) {
      this.$bvToast.toast(content, { title, variant, autoHideDelay: 5000, appendToast: true });
    },
  },
};
</script>
